import React, { useState, useEffect, Suspense, lazy } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { logLogout } from './utils/logger';
import { auth } from './utils/firebase';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getDoc, getDocs, doc, collection, onSnapshot, query, where, setDoc } from 'firebase/firestore';
import { db } from './utils/firebase';
import { collectionPath, documentPath } from './utils/firestoreCollections';
import EmailCampaignPage from './pages/EmailCampaignPage';
import EmailSegmentsPage from './pages/EmailSegmentsPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import CampaignAnalyticsPage from './pages/CampaignAnalyticsPage';
import EmailMarketingAnalyticsPage from './pages/EmailMarketingAnalyticsPage';
import RoleManagementPage from './pages/RoleManagementPage';
import ExportJobsPage from './pages/ExportJobsPage';
import { usePermissions } from './hooks/usePermissions';
import { FEATURE_PERMISSIONS, PAGE_PERMISSIONS } from './utils/permissions';
import { FirestoreHealthChecker } from './utils/errorHandling';

// Page Components
import LoginScreen from './pages/LoginScreen';
import DashboardPage from './pages/DashboardPage';
import CustomerPage from './pages/CustomerPage.jsx';
import CustomerDetailPage from './pages/CustomerDetailPage';
import OppPage from './pages/OppPage.jsx';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import SalesRepPage from './pages/SalesRepPage';
import ReportsDashboard from './pages/ReportsDashboard';
import SalesRepReportsPage from './pages/SalesRepReportsPage';
import CustomerReportsPage from './pages/CustomerReportsPage';
import OrderReportsPage from './pages/OrderReportsPage';
import TaskReportsPage from './pages/TaskReportsPage';
import OpportunityReportsPage from './pages/OpportunityReportsPage';
import CustomerInactivityReportPage from './pages/CustomerInactivityReportPage';
import UserAdminPage from './pages/UserAdminPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import PlatformControlsPage from './pages/PlatformControlsPage';
import CalendarCleanupPage from './pages/CalendarCleanupPage';
import WorkflowEditorPage from './pages/WorkflowEditorPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import AdminOverviewPage from './pages/AdminOverviewPage';
import LogManagementPage from './pages/LogManagementPage';
import ContactsPage from './pages/ContactsPage';
import SalesRepDetailPage from './pages/SalesRepDetailPage';
import ContactDetailPage from './pages/ContactDetailPage.jsx';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskPage from './pages/TaskPage.jsx';
import OppDetailPage from './pages/OppDetailPage.jsx';
import OpportunityModal from './components/forms/OppModal.jsx';
import { useParams } from 'react-router-dom';
import UserProfilePage from './pages/UserProfilePage';
import SupportPage from './pages/SupportPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import NotificationsPage from './pages/NotificationsPage';
import ChatbotAdminPage from './pages/ChatbotAdminPage';
import KnowledgeManagementPage from './pages/KnowledgeManagementPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AchievementManagementPage from './pages/AchievementManagementPage';
import CalendarPage from './pages/CalendarPage';
import RecommendationsPage from './pages/RecommendationsPage';
import DataSyncPage from './pages/DataSyncPage';
import QuoteBuilderPage from './pages/QuoteBuilderPage';
import QuoteSettingsPage from './pages/QuoteSettingsPage';

// Chatbot Components
import { ChatbotProvider } from './components/chatbot/ChatbotContext';
import ChatbotWidget from './components/chatbot/ChatbotWidget';

// Gamification Components
import { GamificationProvider } from './components/gamification/GamificationContext';

// Common Components
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider } from './components/auth/AuthContext';

// Lazy load ComparisonReportsPage to improve initial load time
const ComparisonReportsPage = lazy(() => import('./pages/ComparisonReportsPage'));

function OpportunityEditWrapper({ users, customers, user }) {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [opportunity, setOpportunity] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOpportunity = async () => {
            const docRef = doc(db, collectionPath('opportunities'), id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setOpportunity({ id: docSnap.id, ...docSnap.data() });
            }
            setLoading(false);
        };
        fetchOpportunity();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (!opportunity) return <div>Opportunity not found.</div>;

    return (
        <OpportunityModal
            user={user}
            users={users}
            customers={customers}
            initial={opportunity}
            onSaved={() => navigate(`/opportunity/${opportunity.id}`)}
            onClose={() => navigate(`/opportunity/${opportunity.id}`)}
        />
    );
}

const App = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabledMessage, setDisabledMessage] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    
    // Impersonation state — persists across refresh via sessionStorage
    const [impersonatedUser, setImpersonatedUser] = useState(() => {
        try {
            const stored = sessionStorage.getItem('impersonatedUser');
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });
    const effectiveUser = impersonatedUser || user;
    
    const { can, loading: permissionsLoading } = usePermissions(effectiveUser);
    const [firestoreHealthy, setFirestoreHealthy] = useState(true);

    // Theme state removed (light mode enforced)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Firestore health monitoring - runs every 60 seconds (only when user is authenticated)
    useEffect(() => {
        if (!user) return; // Don't run health checks until user is authenticated
        
        const stopHealthCheck = FirestoreHealthChecker.runPeriodicCheck(
            db,
            60000, // Check every 60 seconds
            (health) => {
                if (!health.healthy) {
                    setFirestoreHealthy(false);
                    console.error('Firestore health check failed:', {
                        action: 'HEALTH_CHECK',
                        readLatency: health.readLatency,
                        status: health.status,
                        timestamp: health.timestamp,
                        error: 'Health check failed'
                    });
                    
                    // Show user-facing warning if prolonged issues
                    if (health.consecutiveFailures > 2) {
                        console.warn('Multiple Firestore failures detected:', {
                            action: 'HEALTH_CHECK_FAILURES',
                            consecutiveFailures: health.consecutiveFailures,
                            timestamp: new Date().toISOString()
                        });
                    }
                } else {
                    setFirestoreHealthy(true);
                    if (health.readLatency > 2000) {
                        console.warn('Firestore slow response detected:', {
                            action: 'SLOW_RESPONSE',
                            latency: health.readLatency,
                            threshold: 2000,
                            timestamp: new Date().toISOString()
                        });
                    }
                }
            }
        );
        
        return () => stopHealthCheck();
    }, [user]); // Add user dependency

    useEffect(() => {
        let unsubUser = null;
        
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            // Clean up previous user listener
            if (unsubUser) {
                unsubUser();
                unsubUser = null;
            }
            
            setLoading(true);
            if (currentUser) {
                try {
                    const usersCollectionPath = collectionPath('users');
                    const userDocRef = doc(db, usersCollectionPath, currentUser.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    
                    if (userDocSnap.exists()) {
                        // Block disabled users before setting up listeners
                        if (userDocSnap.data().role === 'disabled') {
                            await signOut(auth);
                            setUser(null);
                            setDisabledMessage(true);
                            setLoading(false);
                            return;
                        }
                        
                        unsubUser = onSnapshot(userDocRef, (userDoc) => {
                            if (userDoc.exists()) {
                                const userData = { ...currentUser, ...userDoc.data(), uid: currentUser.uid };
                                setUser(userData);
                            } else {
                                console.warn('[App] User document was deleted');
                                setUser({ ...currentUser, uid: currentUser.uid });
                            }
                            setLoading(false);
                        }, (err) => {
                            console.error('User document snapshot error:', err);
                            setUser({ ...currentUser, uid: currentUser.uid });
                            setLoading(false);
                        });
                    } else {
                        // Document ID doesn't match, try querying by uid field
                        const userQuery = query(
                            collection(db, usersCollectionPath),
                            where('uid', '==', currentUser.uid)
                        );
                        
                        const userSnapshot = await getDocs(userQuery);
                        
                        if (!userSnapshot.empty) {
                            const foundDocRef = userSnapshot.docs[0].ref;
                            
                            unsubUser = onSnapshot(foundDocRef, (userDoc) => {
                                if (userDoc.exists()) {
                                    const userData = { ...currentUser, ...userDoc.data(), uid: currentUser.uid };
                                    setUser(userData);
                                } else {
                                    setUser({ ...currentUser, uid: currentUser.uid });
                                }
                                setLoading(false);
                            }, (err) => {
                                console.error('User document snapshot error:', err);
                                setUser({ ...currentUser, uid: currentUser.uid });
                                setLoading(false);
                            });
                        } else {
                            setUser({ ...currentUser, uid: currentUser.uid });
                            setLoading(false);
                        }
                    }
                } catch (err) {
                    console.error('Error setting up user listener:', err);
                    setUser({ ...currentUser, uid: currentUser.uid });
                    setLoading(false);
                }
            } else {
                setUser(null);
                setLoading(false);
            }
        });
        
        return () => {
            unsubscribe();
            if (unsubUser) unsubUser();
        };
    }, []);

    useEffect(() => {
        if (!user) return; // wait for authenticated user

        // customers: admins/impersonating get all, others get only assigned customers
        // When impersonating, always fetch all (admin has access); page-level RBAC handles the view
        let unsubCustomers = () => {};
        try {
            if (can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) || impersonatedUser) {
                unsubCustomers = onSnapshot(
                    collection(db, collectionPath('customers')),
                    (snapshot) => setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
                    (err) => console.error('Customers snapshot error:', {
                        action: 'SNAPSHOT_CUSTOMERS',
                        error: err.message,
                        code: err.code,
                        timestamp: new Date().toISOString()
                    })
                );
            } else {
                const q = query(collection(db, collectionPath('customers')), where('uid', '==', user.uid));
                unsubCustomers = onSnapshot(
                    q,
                    (snapshot) => setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
                    (err) => console.error('Customers (scoped) snapshot error:', {
                        action: 'SNAPSHOT_CUSTOMERS_SCOPED',
                        error: err.message,
                        code: err.code,
                        userId: user?.uid,
                        timestamp: new Date().toISOString()
                    })
                );
            }
        } catch (err) {
            console.error('Failed to subscribe to customers:', {
                action: 'SUBSCRIBE_CUSTOMERS',
                error: err.message,
                code: err.code,
                timestamp: new Date().toISOString()
            });
        }
        return () => unsubCustomers();
    }, [user, can, impersonatedUser]);

    useEffect(() => {
        if (!user) return;
        // Only users with USER_VIEW_ALL permission should subscribe to full users collection.
        // When impersonating, always fetch all (admin has access).
        if (can(FEATURE_PERMISSIONS.USER_VIEW_ALL) || impersonatedUser) {
            const unsubUsers = onSnapshot(
                collection(db, collectionPath('users')),
                (snapshot) => setUsers(snapshot.docs.map(d => ({ uid: d.id, ...d.data() }))),
                (err) => console.error('Users snapshot error:', {
                    action: 'SNAPSHOT_USERS',
                    error: err.message,
                    code: err.code,
                    timestamp: new Date().toISOString()
                })
            );
            return () => unsubUsers();
        } else {
            // For users without permission, just set their own user doc in the users array
            getDoc(doc(db, collectionPath('users', user.uid))).then(snap => {
                if (snap.exists()) setUsers([{ uid: snap.id, ...snap.data() }]);
                else setUsers([]);
            });
        }
    }, [user, can, impersonatedUser]);

    const handleLogout = async () => {
        try {
            // Log logout BEFORE signing out (while user is still authenticated)
            if (user) {
                await logLogout(user).catch(e => 
                    console.warn('Failed to log logout event:', e)
                );
            }
            setImpersonatedUser(null); // Clear impersonation on logout
            try { sessionStorage.removeItem('impersonatedUser'); } catch {}
            await signOut(auth);
        } catch (error) {
            console.error('Sign out failed:', {
                action: 'SIGN_OUT',
                error: error.message,
                code: error.code,
                timestamp: new Date().toISOString()
            });
        }
    };

    // Admin impersonation: view the app as another user
    const handleStartImpersonation = (targetUser) => {
        if (!user || user.role !== 'admin') return;
        if (targetUser.uid === user.uid) return; // Can't impersonate self
        setImpersonatedUser(targetUser);
        try { sessionStorage.setItem('impersonatedUser', JSON.stringify(targetUser)); } catch {}
    };

    const handleStopImpersonation = () => {
        setImpersonatedUser(null);
        try { sessionStorage.removeItem('impersonatedUser'); } catch {}
        window.location.href = '/admin/users';
    };

    if (loading || permissionsLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <LoginScreen disabledMessage={disabledMessage} clearDisabledMessage={() => setDisabledMessage(false)} />;
    }

    return (
        <AuthProvider impersonatedUser={impersonatedUser}>
            <GamificationProvider>
            <ChatbotProvider>
                <ErrorBoundary>
                    <div className="min-h-screen bg-gray-100 font-sans overflow-x-hidden">
                        <div className="flex min-h-screen">
                        {/* Sidebar */}
                        <Sidebar
                            user={user}
                            can={can}
                            mobileMenuOpen={mobileMenuOpen}
                            setMobileMenuOpen={setMobileMenuOpen}
                        />

                        {/* Main Content Area - min-w-0 allows flex item to shrink below content size */}
                        <div className="flex flex-col flex-grow min-w-0 overflow-x-hidden">
                            {/* Header */}
                            <Header
                                user={user}
                                onLogout={handleLogout}
                                mobileMenuOpen={mobileMenuOpen}
                                setMobileMenuOpen={setMobileMenuOpen}
                            />

                            {/* Impersonation Banner */}
                            {impersonatedUser && (
                                <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium shadow-sm">
                                    <span>
                                        Viewing as: <strong>{impersonatedUser.displayName || impersonatedUser.email}</strong>
                                        {impersonatedUser.full_name && impersonatedUser.full_name !== impersonatedUser.displayName && (
                                            <span className="ml-1 opacity-80">({impersonatedUser.full_name})</span>
                                        )}
                                        {impersonatedUser.role && <span className="ml-2 bg-amber-600 px-1.5 py-0.5 rounded text-xs">{impersonatedUser.role}</span>}
                                    </span>
                                    <button
                                        onClick={handleStopImpersonation}
                                        className="bg-white text-amber-700 px-3 py-1 rounded text-xs font-semibold hover:bg-amber-50 transition-colors"
                                    >
                                        Stop Impersonating
                                    </button>
                                </div>
                            )}

                            {/* Page Content */}
                            <Routes>
                                {/* Redirect root to dashboard */}
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.DASHBOARD}>
                                            <DashboardPage user={effectiveUser} key="dashboard" />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/contacts"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.CONTACTS}>
                                            <ContactsPage user={effectiveUser} customers={customers} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/customer"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.CUSTOMERS}>
                                            <CustomerPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/customer/:customerid"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.CUSTOMERS}>
                                            <CustomerDetailPage
                                                user={effectiveUser}
                                                onBack={() => {}}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/opportunities"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.OPPORTUNITIES}>
                                            <OppPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/opportunity/:id"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.OPPORTUNITIES}>
                                            <OppDetailPage user={effectiveUser} users={users} customers={customers} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/opportunities/edit/:id"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.OPPORTUNITIES}>
                                            <OpportunityEditWrapper user={effectiveUser} users={users} customers={customers} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/orders"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.ORDERS}>
                                            <OrdersPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/order/:orderid"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.ORDERS}>
                                            <OrderDetailPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sales-reps"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.SALES_REPS}>
                                            <SalesRepPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sales-reps/:uid"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.SALES_REPS}>
                                            <SalesRepDetailPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <ReportsDashboard user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/reps"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <SalesRepReportsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/customers"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <CustomerReportsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/orders"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <OrderReportsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/tasks"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <TaskReportsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/opportunities"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <OpportunityReportsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/inactivity"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <CustomerInactivityReportPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reports/comparison"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.REPORTS}>
                                            <Suspense fallback={
                                                <div className="flex items-center justify-center min-h-screen">
                                                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
                                                </div>
                                            }>
                                                <ComparisonReportsPage user={effectiveUser} />
                                            </Suspense>
                                        </ProtectedRoute>
                                    }
                                />
                                
                                {/* Admin Routes - New structure */}
                                <Route path="/platform-controls" element={<PlatformControlsPage />} />
                                <Route
                                    path="/admin/quote-settings"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <QuoteSettingsPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermissions={[PAGE_PERMISSIONS.USER_ADMIN, PAGE_PERMISSIONS.ROLE_MANAGEMENT]}>
                                            <AdminOverviewPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <UserAdminPage user={user} onImpersonate={handleStartImpersonation} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/roles"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.ROLE_MANAGEMENT}>
                                            <RoleManagementPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/notifications"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <NotificationSettingsPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/system-settings"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <SystemSettingsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/logs"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <LogManagementPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/calendar-cleanup"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <CalendarCleanupPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/workflows"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <WorkflowEditorPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/chatbot"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <ChatbotAdminPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/knowledge"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <KnowledgeManagementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/data-sync"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={FEATURE_PERMISSIONS.DATA_SYNC_VIEW}>
                                            <DataSyncPage />
                                        </ProtectedRoute>
                                    }
                                />
                                
                                {/* Legacy route redirects for backwards compatibility */}
                                <Route path="/admin-users" element={<Navigate to="/admin/users" replace />} />
                                <Route path="/role-management" element={<Navigate to="/admin/roles" replace />} />
                                <Route path="/notification-settings" element={<Navigate to="/admin/notifications" replace />} />
                                
                                <Route 
                                    path="/customer/:customerId/contact/:contactid" 
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.CONTACTS}>
                                            <ContactDetailPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/tasks/:id"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.TASKS}>
                                            <TaskDetailPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/tasks"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.TASKS}>
                                            <TaskPage user={effectiveUser} showToast={undefined} />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/profile"
                                    element={<UserProfilePage />}
                                />
                                <Route
                                    path="/notifications"
                                    element={
                                        <ProtectedRoute user={user} loading={loading}>
                                            <NotificationsPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/support"
                                    element={
                                        <ProtectedRoute user={user} loading={loading}>
                                            <SupportPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/support/release-notes"
                                    element={
                                        <ProtectedRoute user={user} loading={loading}>
                                            <ReleaseNotesPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/email-campaigns" element={<ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.EMAIL_CAMPAIGNS}><EmailCampaignPage /></ProtectedRoute>} />
                            <Route path="/email-segments" element={<ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.EMAIL_SEGMENTS}><EmailSegmentsPage /></ProtectedRoute>} />
                            <Route path="/email-templates" element={<ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.EMAIL_TEMPLATES}><EmailTemplatesPage /></ProtectedRoute>} />
                            <Route path="/email-marketing-analytics" element={<ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.EMAIL_ANALYTICS}><EmailMarketingAnalyticsPage /></ProtectedRoute>} />
                            <Route path="/campaigns/:campaignId/analytics" element={<ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.EMAIL_ANALYTICS}><CampaignAnalyticsPage /></ProtectedRoute>} />
                            <Route path="/export-jobs" element={<ProtectedRoute user={user} loading={loading}><ExportJobsPage /></ProtectedRoute>} />
                            <Route
                                    path="/leaderboard"
                                    element={
                                        <ProtectedRoute user={user} loading={loading}>
                                            <LeaderboardPage />
                                        </ProtectedRoute>
                                    }
                                />
                            <Route
                                    path="/admin/achievements"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.USER_ADMIN}>
                                            <AchievementManagementPage />
                                        </ProtectedRoute>
                                    }
                                />
                            {/* Calendar */}
                            <Route
                                    path="/calendar"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.CALENDAR}>
                                            <CalendarPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                            {/* AI Sales Agent add-on (page also checks the add-on gate) */}
                            <Route
                                    path="/recommendations"
                                    element={
                                        <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.RECOMMENDATIONS}>
                                            <RecommendationsPage user={effectiveUser} />
                                        </ProtectedRoute>
                                    }
                                />
                            <Route
                                    path="/quotes/new"
                                    element={
                                       <ProtectedRoute user={user} loading={loading}>
                                            <QuoteBuilderPage />
                                        </ProtectedRoute>
                                    }
                                />
                            <Route
                                    path="/quotes/:quoteId"
                                    element={
                                       <ProtectedRoute user={user} loading={loading}>
                                            <QuoteBuilderPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Routes>

                            
                        </div>
                    </div>

                    {/* Chatbot Widget - appears on all pages */}
                    <ChatbotWidget />
                </div>
            </ErrorBoundary>
        </ChatbotProvider>
        </GamificationProvider>
    </AuthProvider>
    );
};

export default App;
